import type { VoiceParticipantDTO, VoiceSignalData } from '@clanfire/shared';
import { userApi } from '@/api/endpoints';
import { clientConfig } from '@/lib/session';
import { useAuth } from '@/stores/auth';
import { hasBlazeNow } from '@/stores/blaze';
import { useVoice } from '@/stores/realtime';
import { useGettingStarted } from '@/stores/gettingStarted';
import { toast } from '@/stores/ui';
import { useVoiceSettings } from '@/stores/voiceSettings';
import { playSound } from '@/lib/sounds';
import { applyOutputDevice, cameraConstraints, MicPipeline, registerLevelProvider } from './audio';
import { getSocket } from './socket';

/**
 * Every connection carries the same four transceivers, in this order. Both
 * sides map them by index, so each media kind has a fixed slot and turning a
 * source on/off is only ever a `replaceTrack` (no renegotiation).
 */
const SLOTS = ['mic', 'camera', 'screen', 'screenAudio'] as const;
type Slot = (typeof SLOTS)[number];
const SLOT_KIND: Record<Slot, 'audio' | 'video'> = { mic: 'audio', camera: 'video', screen: 'video', screenAudio: 'audio' };

interface Peer {
  pc: RTCPeerConnection;
  audio: HTMLAudioElement;
  /** Plays the sharer's tab/system audio; follows the same per-user volume. */
  screenAudio: HTMLAudioElement;
  pendingCandidates: RTCIceCandidateInit[];
  /** We sent the first offer, so we also own ICE restarts (never both sides at once). */
  initiator: boolean;
  senders: Partial<Record<Slot, RTCRtpSender>>;
}

export const screenShareSupported = () => typeof navigator !== 'undefined' && !!navigator.mediaDevices?.getDisplayMedia;

/**
 * Mesh WebRTC voice & video: every participant holds a direct connection to
 * every other participant. The newcomer sends offers to existing members, so
 * two peers never offer to each other at the same time (no "glare").
 *
 * Each connection is created with a fixed set of transceivers up front (see
 * SLOTS). Turning the camera or screen share on/off, or switching
 * microphones, then only swaps tracks and never needs renegotiation.
 * Works well for small rooms (≈ up to 8 people); larger rooms need an SFU.
 */
class VoiceManager {
  private pipeline: MicPipeline | null = null;
  private camera: MediaStream | null = null;
  private screen: MediaStream | null = null;
  private peers = new Map<string, Peer>();
  private joining = false;
  private unsubscribers: Array<() => void> = [];

  constructor() {
    // Settings screens read the live level from an active call.
    registerLevelProvider(() => this.pipeline);
  }

  get channelId() {
    return useVoice.getState().channelId;
  }

  async join(channelId: string, serverId: string) {
    const socket = getSocket();
    if (!socket || this.joining) return;
    if (this.channelId === channelId) return;
    if (this.channelId) this.leave({ silent: true });

    this.joining = true;
    useVoice.getState().setSelf({ channelId, serverId, status: 'connecting' });

    try {
      // Relay (TURN) passwords expire, so each call gets fresh ones. Old ones still work for STUN-only setups.
      await userApi
        .iceServers()
        .then((r) => void (clientConfig.iceServers = r.iceServers))
        .catch(() => undefined);
      await this.startPipeline();
      const result = await socket.timeout(8000).emitWithAck('voice:join', { channelId });
      if (!result.ok) throw new Error(result.error);
      useVoice.getState().setParticipants(channelId, result.participants);
      useVoice.getState().setSelf({ status: 'connected' });
      useGettingStarted.getState().markStep('voice');
      this.watchSettings();
      this.pushState();
      playSound('join');
      this.knownParticipants = new Map(result.participants.map((p: VoiceParticipantDTO) => [p.userId, p.screen]));
      for (const peerId of result.peers) void this.callPeer(peerId);
    } catch (err) {
      this.teardown();
      useVoice.getState().setSelf({ channelId: null, serverId: null, status: 'idle' });
      toast.error(err instanceof Error && err.message ? err.message : 'Could not join the voice channel.');
    } finally {
      this.joining = false;
    }
  }

  leave(opts: { silent?: boolean } = {}) {
    if (!this.channelId && !this.pipeline) return;
    getSocket()?.emit('voice:leave');
    this.teardown();
    useVoice.getState().setSelf({ channelId: null, serverId: null, status: 'idle' });
    if (!opts.silent) playSound('leave', 0.45, { evenIfDeafened: true });
  }

  /** The server removed us (kicked, channel deleted, joined elsewhere…). */
  forceLeft(reason: string) {
    this.teardown();
    useVoice.getState().setSelf({ channelId: null, serverId: null, status: 'idle' });
    toast.info(reason);
  }

  /** After a socket reconnect the server has forgotten us: rejoin the same channel. */
  onReconnect() {
    const { channelId, serverId } = useVoice.getState();
    if (!channelId || !serverId) return;
    this.teardown();
    useVoice.getState().setSelf({ channelId: null, serverId: null, status: 'idle' });
    void this.join(channelId, serverId);
  }

  setMuted(muted: boolean) {
    const { deafened } = useVoice.getState();
    if (this.channelId) playSound(muted ? 'mute' : 'unmute', 0.45, { evenIfDeafened: true });
    // Unmuting while deafened also undeafens, like other voice apps.
    useVoice.getState().setSelf({ muted, deafened: muted ? deafened : false });
    this.applyLocalState();
  }

  setDeafened(deafened: boolean) {
    if (this.channelId) playSound(deafened ? 'deafen' : 'undeafen', 0.45, { evenIfDeafened: true });
    useVoice.getState().setSelf({ deafened });
    this.applyLocalState();
  }

  async setVideo(on: boolean) {
    if (!this.channelId) return;
    if (on) {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: cameraConstraints(useVoiceSettings.getState()) });
        this.stopCamera();
        this.camera = stream;
        const track = stream.getVideoTracks()[0]!;
        track.onended = () => void this.setVideo(false); // camera unplugged or revoked
        for (const peer of this.peers.values()) await peer.senders.camera?.replaceTrack(track);
        useVoice.getState().setSelf({ video: true, localVideo: stream });
      } catch {
        toast.error("Couldn't start your camera. Check that it's connected and that the browser has permission.");
        return;
      }
    } else {
      this.stopCamera();
      for (const peer of this.peers.values()) await peer.senders.camera?.replaceTrack(null);
      useVoice.getState().setSelf({ video: false, localVideo: null });
    }
    this.pushState();
  }

  async setScreenShare(on: boolean) {
    if (!this.channelId) return;
    if (on) {
      if (!screenShareSupported()) {
        toast.error("Your browser doesn't support screen sharing.");
        return;
      }
      let stream: MediaStream;
      try {
        // Audio is offered for tabs/whole screens where the browser supports it.
        // Capped at 1080p: bigger captures (1440p/4K monitors) can't be encoded at a
        // sane bitrate without dropping to a few frames per second. Blaze gets 60 fps.
        const fps = hasBlazeNow() ? 60 : 30;
        stream = await navigator.mediaDevices.getDisplayMedia({
          video: { frameRate: { ideal: fps, max: fps }, width: { max: 1920 }, height: { max: 1080 } },
          audio: true,
        });
      } catch (err) {
        // Cancelling the browser's picker is not an error worth shouting about.
        if (!(err instanceof DOMException && err.name === 'NotAllowedError')) toast.error("Couldn't start screen sharing.");
        return;
      }
      if (!this.channelId) {
        // Left the call while the picker was open.
        stream.getTracks().forEach((t) => t.stop());
        return;
      }
      this.stopScreen();
      this.screen = stream;
      const video = stream.getVideoTracks()[0]!;
      video.contentHint = 'detail'; // favour sharp text over smooth motion
      video.onended = () => void this.setScreenShare(false); // the browser's own "Stop sharing" button
      const audio = stream.getAudioTracks()[0] ?? null;
      for (const peer of this.peers.values()) await this.sendScreen(peer, video, audio);
      const surface = (video.getSettings() as MediaTrackSettings & { displaySurface?: string }).displaySurface ?? null;
      useVoice.getState().setSelf({ screen: true, localScreen: stream, screenSurface: surface });
      playSound('stream-start');
    } else {
      this.stopScreen();
      for (const peer of this.peers.values()) await this.sendScreen(peer, null, null);
      useVoice.getState().setSelf({ screen: false, localScreen: null, screenSurface: null });
      playSound('stream-end');
    }
    this.pushState();
  }

  onParticipants(channelId: string, participants: VoiceParticipantDTO[]) {
    if (channelId !== this.channelId) return;
    this.announceChanges(participants);
    const present = new Set(participants.map((p) => p.userId));
    for (const peerId of [...this.peers.keys()]) if (!present.has(peerId)) this.closePeer(peerId);
  }

  async onSignal(from: string, data: VoiceSignalData) {
    if (!this.channelId || !this.pipeline) return;
    try {
      if (data.type === 'offer') {
        const existing = this.peers.get(from);
        const peer = existing ?? this.createPeer(from, false);
        await peer.pc.setRemoteDescription({ type: 'offer', sdp: data.sdp });
        if (!existing) await this.attachLocalTracks(peer);
        await this.flushCandidates(peer);
        const answer = await peer.pc.createAnswer();
        await peer.pc.setLocalDescription(answer);
        this.signal(from, { type: 'answer', sdp: answer.sdp ?? '' });
      } else if (data.type === 'answer') {
        const peer = this.peers.get(from);
        if (!peer || peer.pc.signalingState !== 'have-local-offer') return;
        await peer.pc.setRemoteDescription({ type: 'answer', sdp: data.sdp });
        await this.flushCandidates(peer);
        if (this.screen) await this.sendScreen(peer, this.localTrack('screen'), this.localTrack('screenAudio'));
      } else if (data.type === 'candidate') {
        const peer = this.peers.get(from);
        if (!peer) return;
        if (peer.pc.remoteDescription) await peer.pc.addIceCandidate(data.candidate);
        else peer.pendingCandidates.push(data.candidate);
      }
    } catch (err) {
      console.warn('[voice] signalling error', err);
    }
  }

  /* ---------------------------------------------------------------- */

  private async startPipeline() {
    this.pipeline = new MicPipeline();
    const micOk = await this.pipeline.start();
    this.pipeline.subscribe((report) => this.setSpeaking(report.speaking));
    if (!micOk) {
      // Join anyway in listen-only mode.
      useVoice.getState().setSelf({ micUnavailable: true, muted: true });
      toast.error('Microphone unavailable — you joined in listen-only mode. Check your browser permissions or Voice & Video settings.');
    } else {
      useVoice.getState().setSelf({ micUnavailable: false });
    }
    this.applyLocalState();
  }

  /** Applies speaker/camera setting changes to the live call. */
  private watchSettings() {
    this.unsubscribers.push(
      useVoiceSettings.subscribe((s, prev) => {
        if (
          s.outputDeviceId !== prev.outputDeviceId ||
          s.outputVolume !== prev.outputVolume ||
          s.userVolumes !== prev.userVolumes ||
          s.localMutes !== prev.localMutes
        ) {
          for (const [peerId, peer] of this.peers) this.configurePeerOutput(peer, peerId);
        }
        if (s.videoDeviceId !== prev.videoDeviceId && this.camera) void this.setVideo(true);
      }),
    );
  }

  private configurePeerOutput(peer: Peer, peerId: string) {
    this.configureOutput(peer.audio, peerId);
    this.configureOutput(peer.screenAudio, peerId);
  }

  /** Output volume × per-user volume; muted when deafened or locally muted. */
  private configureOutput(audio: HTMLAudioElement, peerId: string) {
    const s = useVoiceSettings.getState();
    const userVolume = s.userVolumes[peerId] ?? 100;
    audio.volume = Math.min(1, Math.max(0, (s.outputVolume / 100) * (userVolume / 100)));
    audio.muted = useVoice.getState().deafened || !!s.localMutes[peerId];
    void applyOutputDevice(audio, s.outputDeviceId);
  }

  private createPeer(peerId: string, initiator: boolean): Peer {
    const pc = new RTCPeerConnection({ iceServers: clientConfig.iceServers });
    const audio = new Audio();
    audio.autoplay = true;
    const screenAudio = new Audio();
    screenAudio.autoplay = true;
    const peer: Peer = { pc, audio, screenAudio, pendingCandidates: [], initiator, senders: {} };
    this.configurePeerOutput(peer, peerId);
    this.peers.set(peerId, peer);

    pc.onicecandidate = (e) => {
      if (!e.candidate) return;
      const { candidate, sdpMid, sdpMLineIndex, usernameFragment } = e.candidate;
      this.signal(peerId, { type: 'candidate', candidate: { candidate, sdpMid, sdpMLineIndex, usernameFragment } });
    };
    pc.ontrack = (e) => {
      const stream = new MediaStream([e.track]);
      const slot = SLOTS[pc.getTransceivers().indexOf(e.transceiver)];
      if (slot === 'mic' || slot === 'screenAudio') {
        const el = slot === 'mic' ? audio : screenAudio;
        el.srcObject = stream;
        void el.play().catch(() => undefined);
      } else if (slot === 'camera') {
        useVoice.getState().setRemoteVideo(peerId, stream);
        e.track.onended = () => useVoice.getState().setRemoteVideo(peerId, null);
      } else if (slot === 'screen') {
        useVoice.getState().setRemoteScreen(peerId, stream);
        e.track.onended = () => useVoice.getState().setRemoteScreen(peerId, null);
      }
    };
    pc.onconnectionstatechange = () => {
      // Only the side that made the original offer restarts ICE, so restarts never collide.
      if (pc.connectionState === 'failed' && peer.initiator) void this.restartIce(peerId);
    };
    return peer;
  }

  /** The track we currently send in each slot (null = slot idle). */
  private localTrack(slot: Slot): MediaStreamTrack | null {
    switch (slot) {
      case 'mic':
        return this.pipeline?.outputTrack ?? null;
      case 'camera':
        return this.camera?.getVideoTracks()[0] ?? null;
      case 'screen':
        return this.screen?.getVideoTracks()[0] ?? null;
      case 'screenAudio':
        return this.screen?.getAudioTracks()[0] ?? null;
    }
  }

  /** Answerer side: send our tracks on the transceivers the offer created. */
  private async attachLocalTracks(peer: Peer) {
    const transceivers = peer.pc.getTransceivers();
    for (const [i, slot] of SLOTS.entries()) {
      const transceiver = transceivers[i];
      if (!transceiver) continue;
      transceiver.direction = 'sendrecv';
      await transceiver.sender.replaceTrack(this.localTrack(slot));
      peer.senders[slot] = transceiver.sender;
    }
  }

  private async sendScreen(peer: Peer, video: MediaStreamTrack | null, audio: MediaStreamTrack | null) {
    await peer.senders.screen?.replaceTrack(video);
    await peer.senders.screenAudio?.replaceTrack(audio);
    const sender = peer.senders.screen;
    if (!video || !sender) return;
    try {
      // Screens are mostly static text: keep resolution, allow a generous bitrate (more for Blaze's 60 fps).
      const params = sender.getParameters();
      if (params.encodings?.length) {
        params.encodings[0]!.maxBitrate = hasBlazeNow() ? 6_000_000 : 3_000_000;
        params.degradationPreference = 'maintain-resolution';
        await sender.setParameters(params);
      }
    } catch {
      /* Not negotiated yet or unsupported — defaults are fine. */
    }
  }

  private async callPeer(peerId: string) {
    const peer = this.createPeer(peerId, true);
    try {
      for (const slot of SLOTS) {
        const track = this.localTrack(slot);
        peer.senders[slot] = peer.pc.addTransceiver(track ?? SLOT_KIND[slot], { direction: 'sendrecv' }).sender;
      }
      const offer = await peer.pc.createOffer();
      await peer.pc.setLocalDescription(offer);
      this.signal(peerId, { type: 'offer', sdp: offer.sdp ?? '' });
    } catch (err) {
      console.warn('[voice] could not create offer', err);
      this.closePeer(peerId);
    }
  }

  private async restartIce(peerId: string) {
    const peer = this.peers.get(peerId);
    if (!peer) return;
    try {
      const offer = await peer.pc.createOffer({ iceRestart: true });
      await peer.pc.setLocalDescription(offer);
      this.signal(peerId, { type: 'offer', sdp: offer.sdp ?? '' });
    } catch (err) {
      console.warn('[voice] ICE restart failed', err);
    }
  }

  private async flushCandidates(peer: Peer) {
    for (const candidate of peer.pendingCandidates.splice(0)) {
      await peer.pc.addIceCandidate(candidate).catch(() => undefined);
    }
  }

  private signal(to: string, data: VoiceSignalData) {
    getSocket()?.emit('voice:signal', { to, data });
  }

  private closePeer(peerId: string) {
    const peer = this.peers.get(peerId);
    if (!peer) return;
    peer.pc.close();
    peer.audio.srcObject = null;
    peer.screenAudio.srcObject = null;
    this.peers.delete(peerId);
    useVoice.getState().setRemoteVideo(peerId, null);
    useVoice.getState().setRemoteScreen(peerId, null);
  }

  private applyLocalState() {
    const { muted, deafened } = useVoice.getState();
    this.pipeline?.setMuted(muted || deafened);
    for (const [peerId, peer] of this.peers) this.configurePeerOutput(peer, peerId);
    if (muted || deafened) this.setSpeaking(false);
    this.pushState();
  }

  private pushState() {
    if (!this.channelId) return;
    const { muted, deafened, video, screen } = useVoice.getState();
    getSocket()?.emit('voice:state', { muted: muted || deafened, deafened, video, screen });
  }

  private speaking = false;
  private setSpeaking(speaking: boolean) {
    if (this.speaking === speaking) return;
    this.speaking = speaking;
    const me = useAuth.getState().user?.id;
    if (me) useVoice.getState().setSpeaking(me, speaking);
    if (this.channelId) getSocket()?.emit('voice:speaking', { speaking });
  }

  private stopCamera() {
    this.camera?.getTracks().forEach((t) => {
      t.onended = null;
      t.stop();
    });
    this.camera = null;
  }

  private stopScreen() {
    this.screen?.getTracks().forEach((t) => {
      t.onended = null;
      t.stop();
    });
    this.screen = null;
  }

  private teardown() {
    this.unsubscribers.forEach((u) => u());
    this.unsubscribers = [];
    for (const peerId of [...this.peers.keys()]) this.closePeer(peerId);
    this.stopCamera();
    this.stopScreen();
    this.pipeline?.stop();
    this.pipeline = null;
    this.setSpeaking(false);
    useVoice.getState().setSelf({ video: false, localVideo: null, screen: false, localScreen: null, screenSurface: null });
    this.knownParticipants.clear();
  }

  /** Who's in our channel (and whether they're live), to announce arrivals, departures and streams. */
  private knownParticipants = new Map<string, boolean>();

  private announceChanges(participants: VoiceParticipantDTO[]) {
    const me = useAuth.getState().user?.id;
    const next = new Map(participants.map((p) => [p.userId, p.screen]));
    let joined = false;
    let left = false;
    let wentLive = false;
    for (const [id, screen] of next) {
      if (id === me) continue;
      if (!this.knownParticipants.has(id)) joined = true;
      else if (screen && !this.knownParticipants.get(id)) wentLive = true;
    }
    for (const id of this.knownParticipants.keys()) if (id !== me && !next.has(id)) left = true;
    this.knownParticipants = next;
    if (joined) playSound('user-join');
    else if (left) playSound('user-leave');
    else if (wentLive) playSound('stream-start', 0.35);
  }
}

export const voiceManager = new VoiceManager();
